import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck, Download, AlertTriangle, Loader2 } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { ROLE_RANK } from '@praxara/shared/constants';

const ACTION_LABELS: Record<string, string> = {
  USER_LOGIN: 'Logged in',
  USER_CREATED: 'User created',
  DOCUMENT_UPLOADED: 'Document uploaded',
  DOCUMENT_PROCESSED: 'Document processed',
  AI_MODEL_CALLED: 'AI model used',
  AI_OUTPUT_GENERATED: 'AI output generated',
  PV_CASE_CREATED: 'Case created',
  PV_CASE_CODED: 'Case coded',
  PV_CASE_REVIEWED: 'Case reviewed',
  PV_CASE_NARRATED: 'Narrative drafted',
  PV_CASE_SUBMITTED: 'Case submitted',
  PV_CASE_CLOSED: 'Case closed',
  EXPORT_GENERATED: 'Export generated',
  EXTRACTION_APPROVED: 'Extraction approved',
  EXTRACTION_CORRECTED: 'Extraction corrected',
  EXTRACTION_REJECTED: 'Extraction rejected',
  WORKFLOW_STARTED: 'Workflow started',
  WORKFLOW_COMPLETED: 'Workflow completed',
  TENANT_PROVISIONED: 'Tenant created',
  ESIGNATURE_APPLIED: 'E-signature applied',
};

function humanizeActionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action.toLowerCase().replace(/_/g, ' ');
}

export function AuditLogPage() {
  const user = useAuthStore((s) => s.user);
  const canManage = user && ROLE_RANK[user.role] >= ROLE_RANK['MANAGER'];
  const [filterAction, setFilterAction] = useState('');
  const [filterResource, setFilterResource] = useState('');

  const logs = useQuery({
    queryKey: ['audit', filterAction, filterResource],
    queryFn: async () => {
      const params = new URLSearchParams({ pageSize: '200' });
      if (filterAction) params.set('action', filterAction);
      if (filterResource) params.set('resource', filterResource);
      return (await api.get(`/api/audit?${params.toString()}`)).data;
    },
  });

  const [integrity, setIntegrity] = useState<{
    valid: boolean;
    totalRecords: number;
    brokenAt: string | null;
    checkedAt: string;
    durationMs?: number;
  } | null>(null);
  const [checking, setChecking] = useState(false);

  async function verify() {
    setChecking(true);
    try {
      const res = await api.get('/api/audit/verify');
      setIntegrity(res.data);
      const ms = typeof res.data.durationMs === 'number' ? res.data.durationMs : 0;
      if (res.data.valid) {
        toast.success(
          'Audit chain VALID',
          `${res.data.totalRecords} records · ${ms}ms`,
        );
      } else {
        toast.error(
          'Chain BROKEN',
          `at record ${res.data.brokenAt ?? 'unknown'}`,
        );
      }
    } catch (err) {
      toast.error('Verify failed', extractApiError(err).message);
    } finally {
      setChecking(false);
    }
  }

  async function exportCsv() {
    try {
      const res = await api.get('/api/audit/export', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed', extractApiError(err).message);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Audit trail</h1>
          <p className="text-sm text-muted-foreground">
            Immutable log of every action. SHA-256 chained. ALCOA+ compliant.
          </p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button onClick={verify} disabled={checking}>
              {checking ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <ShieldCheck className="mr-1.5 h-4 w-4" />
              )}
              {checking ? 'Verifying...' : 'Verify chain'}
            </Button>
            <Button variant="outline" onClick={exportCsv}>
              <Download className="mr-1.5 h-4 w-4" /> Export CSV
            </Button>
          </div>
        )}
      </div>

      {integrity && (
        <Card className={integrity.valid ? 'border-emerald-500/50' : 'border-destructive'}>
          <CardContent className="flex items-center gap-3 p-4">
            {integrity.valid ? (
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-destructive" />
            )}
            <div className="text-sm">
              {integrity.valid ? (
                <>
                  <span className="font-medium">Chain intact.</span>{' '}
                  {integrity.totalRecords} records verified
                  {typeof integrity.durationMs === 'number' && (
                    <> in {integrity.durationMs}ms</>
                  )}{' '}
                  at {formatDate(integrity.checkedAt)}.
                </>
              ) : (
                <>
                  <span className="font-medium text-destructive">Tampering detected.</span>{' '}
                  Chain broken at record <code>{integrity.brokenAt}</code>.
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center gap-2">
          <Input
            placeholder="Filter action (e.g. DOCUMENT_UPLOADED)"
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="max-w-xs"
          />
          <Input
            placeholder="Filter resource (e.g. document)"
            value={filterResource}
            onChange={(e) => setFilterResource(e.target.value)}
            className="max-w-xs"
          />
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Signed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {logs.data?.items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No audit records match these filters.
                  </TableCell>
                </TableRow>
              )}
              {logs.data?.items?.map((log: {
                id: string;
                createdAt: string;
                action: string;
                resource: string;
                resourceId: string | null;
                description: string;
                signedBy: string | null;
                signatureReason: string | null;
                user: { email: string; role: string } | null;
              }) => (
                <TableRow key={log.id}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {formatDate(log.createdAt)}
                  </TableCell>
                  <TableCell className="text-xs">
                    {log.user ? (
                      <div>
                        <div>{log.user.email}</div>
                        <div className="text-muted-foreground">{log.user.role}</div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">system</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{humanizeActionLabel(log.action)}</Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {log.resource}
                    {log.resourceId && (
                      <div className="text-muted-foreground">
                        {log.resourceId.slice(0, 8)}...
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="max-w-md text-xs">{log.description}</TableCell>
                  <TableCell>
                    {log.signedBy && (
                      <Badge variant="info" title={log.signatureReason ?? undefined}>
                        Signed
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
